import { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QueryProvider } from './app/providers/QueryProvider';
import { AppProvider } from './app/providers';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import WebHotelierErrorBoundary from './features/webhotelier/components/WebHotelierErrorBoundary';
import FallbackCalendar from './components/FallbackCalendar';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Lazy-loaded pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Help = lazy(() => import('./pages/Help'));
const Rooms = lazy(() => import('./pages/Rooms'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Admin = lazy(() => import('./pages/Admin'));
const Properties = lazy(() => import('./pages/Properties'));
const Settings = lazy(() => import('./pages/Settings'));
const ChangeLogExplorer = lazy(() => import('./pages/ChangeLogExplorer'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

function LegacyPricingRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/pricing${search}`} replace />;
}

function App() {
  return (
    <QueryProvider>
      <WebHotelierErrorBoundary>
        <ThemeProvider>
          <WebHotelierErrorBoundary>
            <AppProvider>
              <DndProvider backend={HTML5Backend}>
                <Router>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {/* Public routes (no layout, no protection) */}
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/accept-invite" element={<AcceptInvite />} />
                      <Route
                        path="/reset-password"
                        element={<ResetPassword />}
                      />

                      {/* Protected routes with layout */}
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <Routes>
                                <Route path="/" element={<Dashboard />} />
                                <Route
                                  path="/calendar"
                                  element={
                                    <ErrorBoundary fallback={FallbackCalendar}>
                                      <Calendar />
                                    </ErrorBoundary>
                                  }
                                />
                                <Route
                                  path="/admin"
                                  element={<LegacyPricingRedirect />}
                                />
                                <Route path="/pricing" element={<Admin />} />
                                <Route
                                  path="/analytics"
                                  element={<Dashboard analytics />}
                                />
                                <Route
                                  path="/properties/rooms"
                                  element={<Rooms />}
                                />
                                <Route path="/help" element={<Help />} />
                                <Route
                                  path="/audit"
                                  element={<ChangeLogExplorer />}
                                />
                                <Route
                                  path="/properties"
                                  element={
                                    <WebHotelierErrorBoundary>
                                      <Properties />
                                    </WebHotelierErrorBoundary>
                                  }
                                />
                                <Route
                                  path="/settings"
                                  element={
                                    <WebHotelierErrorBoundary>
                                      <Settings />
                                    </WebHotelierErrorBoundary>
                                  }
                                />
                                {/* Redirect old pricing-analytics route to dashboard */}
                                <Route
                                  path="/pricing-analytics"
                                  element={<Navigate to="/analytics" replace />}
                                />
                                {/* Catch-all redirect to dashboard */}
                                <Route
                                  path="*"
                                  element={<Navigate to="/" replace />}
                                />
                              </Routes>
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                </Router>
              </DndProvider>
            </AppProvider>
          </WebHotelierErrorBoundary>
        </ThemeProvider>
      </WebHotelierErrorBoundary>
    </QueryProvider>
  );
}

export default App;
